import { Dispatch } from "redux";
import { ACTION_TYPES } from "../../constants/actionTypes";
import { REFERRAL_TYPES } from "../../constants/gamification/referralTypes";
import {
  getUserReferralInfo,
  inviteUser as inviteUserApi,
  processReferral as processReferralApi,
  getGamificationSummary as getGamificationSummaryApi,
  removeInvitation,
} from "@api/gamification/referral";

/********************************************
  Get user referral info action
*********************************************/
export const getUserReferralInfoAction = () => async (dispatch: Dispatch) => {
  // Dispatch loading alert
  dispatch({
    type: ACTION_TYPES.ALERT,
    payload: { loading: true },
  });

  try {
    // Fetch response from server
    const res = await getUserReferralInfo();

    // Dispatch referral info
    dispatch({
      type: REFERRAL_TYPES.GET_USER_REFERRAL_INFO,
      payload: res.data,
    });

    // Dispatch success alert
    dispatch({
      type: ACTION_TYPES.ALERT,
      payload: { loading: false },
    });

    return res.data;
  } catch (error) {
    // Dispatch error alert
    dispatch({
      type: ACTION_TYPES.ALERT,
      payload: {
        message: "GET_USER_REFERRAL_INFO_ACTION_ERROR",
        loading: false,
      },
    });

    throw error;
  }
};

/********************************************
  Invite user action
*********************************************/
export const inviteUserAction =
  (email: string) => async (dispatch: Dispatch) => {
    // Dispatch loading alert
    dispatch({
      type: ACTION_TYPES.ALERT,
      payload: { loading: true },
    });

    try {
      // Fetch response from server
      const res = await inviteUserApi({ email });

      // Dispatch invite result
      dispatch({
        type: REFERRAL_TYPES.INVITE_USER,
        payload: res.data,
      });

      // Check if there was an email delivery error
      if (res.data.emailError) {
        // Show warning instead of success
        dispatch({
          type: ACTION_TYPES.ALERT,
          payload: {
            loading: false,
            warning: true,
            message: res.data.message,
          },
        });
      } else {
        // Dispatch success alert
        dispatch({
          type: ACTION_TYPES.ALERT,
          payload: {
            loading: false,
            success: true,
            message: res.data.message || "Invitation sent successfully!",
          },
        });
      }

      return res.data;
    } catch (error: any) {
      // Dispatch error alert
      dispatch({
        type: ACTION_TYPES.ALERT,
        payload: {
          error: true,
          message: error.response?.data?.message || "Error sending invitation",
          loading: false,
        },
      });

      // Make sure to rethrow the error with the response data
      if (error.response?.data) {
        error.message = error.response.data.message || error.message;
      }
      throw error;
    }
  };

/********************************************
  Process referral action
*********************************************/
export const processReferralAction =
  (referralCode: string) => async (dispatch: Dispatch) => {
    // Dispatch loading alert
    dispatch({
      type: ACTION_TYPES.ALERT,
      payload: { loading: true },
    });

    try {
      // Fetch response from server
      const res = await processReferralApi({ referralCode });

      // Dispatch process result
      dispatch({
        type: REFERRAL_TYPES.PROCESS_REFERRAL,
        payload: res.data,
      });

      // Dispatch success alert
      dispatch({
        type: ACTION_TYPES.ALERT,
        payload: {
          loading: false,
          success: true,
          message: "Referral processed successfully!",
        },
      });

      return res.data;
    } catch (error: any) {
      // Dispatch error alert
      dispatch({
        type: ACTION_TYPES.ALERT,
        payload: {
          message: error.response?.data?.message || "Error processing referral",
          loading: false,
        },
      });

      throw error;
    }
  };

/********************************************
  Get gamification summary action
*********************************************/
export const getGamificationSummaryAction =
  () => async (dispatch: Dispatch) => {
    // Dispatch loading alert
    dispatch({
      type: ACTION_TYPES.ALERT,
      payload: { loading: true },
    });

    try {
      // Fetch response from server
      const res = await getGamificationSummaryApi();

      // Dispatch gamification summary
      dispatch({
        type: REFERRAL_TYPES.GET_GAMIFICATION_SUMMARY,
        payload: res.data,
      });

      // Dispatch success alert
      dispatch({
        type: ACTION_TYPES.ALERT,
        payload: { loading: false },
      });

      return res.data;
    } catch (error) {
      // Dispatch error alert
      dispatch({
        type: ACTION_TYPES.ALERT,
        payload: {
          message: "GET_GAMIFICATION_SUMMARY_ACTION_ERROR",
          loading: false,
        },
      });

      throw error;
    }
  };

/********************************************
  Remove invitation action
*********************************************/
export const removeInvitationAction =
  (email: string) => async (dispatch: Dispatch) => {
    // Dispatch loading alert
    dispatch({
      type: ACTION_TYPES.ALERT,
      payload: { loading: true },
    });

    try {
      // Fetch response from server
      const res = await removeInvitation({ email });

      // Dispatch remove result
      dispatch({
        type: REFERRAL_TYPES.REMOVE_INVITATION,
        payload: res.data,
      });

      // Dispatch success alert
      dispatch({
        type: ACTION_TYPES.ALERT,
        payload: {
          loading: false,
          success: true,
          message: res.data.message || "Invitation removed successfully!",
        },
      });

      return res.data;
    } catch (error: any) {
      // Dispatch error alert
      dispatch({
        type: ACTION_TYPES.ALERT,
        payload: {
          error: true,
          message: error.response?.data?.message || "Error removing invitation",
          loading: false,
        },
      });

      // Make sure to rethrow the error with the response data
      if (error.response?.data) {
        error.message = error.response.data.message || error.message;
      }
      throw error;
    }
  };